/* Public types for the CodeBaby adapter. The live and mock implementations
 * both conform to this surface; consumers (screens, hooks, tests) import
 * from `@/lib/codebaby`, never from a specific implementation file. */

export type LoaderMode = 'mock' | 'live';

export type PersonaId =
  | 'dan-kowalski'
  | 'rita-solano'
  | 'maya-osei'
  | 'tyler-brandt'
  | 'coach';

export type SessionMode = 'roleplay' | 'debrief' | 'lesson' | 'playbook' | 'dashboard';

/** Anything pushed in startData must stay small (~1-2KB).
 *  Heavy or dynamic context goes through `pushContext` post-mount.
 *  Cf. CODEBABY_GUIDE.md §2 — the 8KB startData URL cap. */
export interface StartData {
  mode: SessionMode;
  personaId?: PersonaId;
  scenarioId?: string;
  learnerId?: string;
  learnerName?: string;
}

export interface MicrophoneData {
  text: string;
  isFinal: boolean;
}

export interface ResponsePayload {
  text: string;
  audioUrl?: string;
}

export interface MountOptions {
  /** DOM container the avatar canvas mounts into. */
  containerEl: HTMLElement;
  /** Loader ID from the CodeBaby portal — see `src/constants/avatars.ts`. */
  avatarLoaderId: string;
  /** Full loader URL when known (e.g. provisioned by the studio against
   *  Bridge3). Takes precedence over avatarLoaderId + the default loader
   *  base URL. The live adapter still appends startData. */
  loaderUrl?: string;
  /** Bootstrap config — keep small. */
  startData: StartData;

  onPlayerBuilt?: () => void;
  onResponse?: (payload: ResponsePayload) => void;
  onInterimResponse?: (text: string) => void;
  onMicrophoneStarted?: () => void;
  onMicrophoneReady?: () => void;
  onMicrophoneStopped?: () => void;
  /** Mic transcript. CRITICAL: do NOT call ask() in response to a final
   *  here; the loader does it itself. Cf. CODEBABY_GUIDE.md §4 — the
   *  auto-ask trap. Use for display only. */
  onMicrophoneData?: (data: MicrophoneData) => void;
  onSegmentEnded?: () => void;
  onError?: (error: Error) => void;
}

/** Interface returned from a successful mount. Live and mock both
 *  conform; switching implementations does not change consumers. */
export interface AvatarSession {
  /** Send a user-authored ask (typed input only — see auto-ask trap). */
  ask(text: string): void;
  /** Speak a scripted line (system-owned; bypasses the dialog flow).
   *  Cf. CODEBABY_GUIDE.md §5 — single-source rule. */
  playResponse(text: string): void;
  /** Toggle mic listening on/off. */
  toggleMicrophone(): void;
  mute(): void;
  unmute(): void;
  /** Clear conversation memory (use sparingly — see §12 #4). */
  resetConversation(): void;
  /** Push runtime context that doesn't fit in startData. */
  pushContext(context: Record<string, unknown>): void;
  /** Detach handlers, close sockets, remove script tag, clear timers.
   *  Do NOT delete window.vidbaby / window.jQuery — §3. */
  unmount(): void;
}

export interface MountResult {
  session: AvatarSession;
}

// =========================================================================
// Comcast-port types — used by the ported lib/ + screens.
//
// Per-response structured metadata Comcast's scoring engine consumes.
// Each persona response is expected to carry a CBClientData object,
// returned as `clientData` on the response envelope. Fields are
// non-omittable — when something doesn't apply, the dialog flow sends
// null rather than dropping the field.
//
// We aren't using the meta-avatar pattern (ADR 0002 — backend
// classifier instead), but ported lib/scoring + lib/roleplay/hint
// type against these shapes. The classifier path will produce them.

import type { Milestone, SessionOutcome } from '@/types/session';
import type { PersonaKey } from '@/constants/avatars';

// ----- startData (mount-time) -----

export interface CBLearner {
  name: string;
  current_module: number;
  current_lesson: number;
}

export interface CBLastSession {
  persona_id: PersonaKey;
  outcome: SessionOutcome;
  score: number;
  attempt_number: number;
}

export interface CBSavedMomentBrief {
  quote: string;
  persona_id: PersonaKey;
  tags: string[];
}

export interface CBCheckInRubric {
  strong: string[];
  weak: string[];
}

export interface CBDebriefVerdict {
  score: number;
  outcome: SessionOutcome;
  milestones_hit: Milestone[];
  milestones_missed: Milestone[];
  moments: Array<{
    type: 'win' | 'growth';
    quote: string;
    at_s: number;
    coach_note_id?: string;
  }>;
}

/** Discriminated union of mount-time payloads for the ported screens.
 *  Each mode carries the minimum context the avatar's dialog flow
 *  needs at script-tag-injection time. Per CODEBABY_GUIDE.md §2, keep
 *  per-mode payloads <1-2KB — anything larger rides
 *  na-conversationalContextData post-mount instead. */
export type CBStartData =
  | {
      mode: 'greeting';
      learner: CBLearner;
      last_session: CBLastSession | null;
      saved_playbook: CBSavedMomentBrief[];
      strengths: string[];
      focus_areas: string[];
    }
  | { mode: 'lesson'; learner: CBLearner; beat_id: string }
  | {
      mode: 'check_in';
      learner: CBLearner;
      beat_id: string;
      beat_rubric: CBCheckInRubric;
    }
  | { mode: 'debrief'; learner: CBLearner; verdict: CBDebriefVerdict }
  | {
      mode: 'roleplay';
      learner: CBLearner;
      prior_attempts: number;
      last_attempt_score: number | null;
      last_attempt_outcome: SessionOutcome | null;
      strengths: string[];
      focus_areas: string[];
    }
  | {
      mode: 'playbook';
      learner: CBLearner;
      saved_moments: CBSavedMomentBrief[];
      total_plays: number;
      unique_skills: number;
    };

export interface CBClientData {
  persona_state: 'waiting' | 'speaking' | 'thinking';
  temperature_delta: number;
  /** Persona-specific milestone tag. Widened from the small Comcast-shape
   *  Milestone enum to `string | null` so the insurance classifier's
   *  MilestoneTag vocab (~30 tags) flows through unchanged. The grading
   *  layer narrows per-scenario via `scenario.milestones.filter`. */
  milestone_tag: string | null;
  objection_raised: string | null;
  objection_handled: string | null;
  close_offered: boolean;
  close_size: 'small' | 'large' | null;
  is_key_moment: boolean;
  moment_type: 'win' | 'growth' | null;
  moment_summary: string | null;
  coach_note_id: string | null;
}

/** Default neutral clientData — used by coach teaching turns. */
export function neutralClientData(): CBClientData {
  return {
    persona_state: 'speaking',
    temperature_delta: 0,
    milestone_tag: null,
    objection_raised: null,
    objection_handled: null,
    close_offered: false,
    close_size: null,
    is_key_moment: false,
    moment_type: null,
    moment_summary: null,
    coach_note_id: null,
  };
}

/** Persona-side scoring signals from the classifier — same shape as
 *  CBClientData minus the persona_state (which is observable from the
 *  loader directly) and minus coach_note_id (coach-side concern). */
export type CBPersonaSignals = Omit<CBClientData, 'persona_state' | 'coach_note_id'>;

/* ------------------------- response envelope ------------------------ */

export interface CBResponse {
  answerText: string;
  clickableResponses?: string[];
  clientData: CBClientData;
  /**
   * True when this response arrived within ~2.5s of the previous response
   * to the same ask — i.e. the dialog flow emitted a second reply for one
   * user turn. Hosts should REPLACE the prior persona transcript turn
   * rather than append, so the displayed text matches whatever the persona
   * ends up saying out loud (the loader's TTS plays the latest one).
   */
  supersedesPrevious?: boolean;
}

/** Coach-side classification metadata. In Comcast's deployment this
 *  came from a secondary "meta-avatar" socket; per ADR 0002 we
 *  produce it from our backend classifier. The MetaCueChip component
 *  types against this. */
export interface CoachMeta {
  mode_handled: 'greeting' | 'lesson' | 'check_in' | 'debrief' | 'playbook';
  response_type: 'narrate' | 'answer' | 'redirect' | 'affirm' | 'challenge' | 'clarify';
  referenced_moment_id: string | null;
  suggested_next_action: string | null;
}
